import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Typography, Button, Breadcrumb, Space, message, Tooltip } from 'antd';
import { HomeOutlined, ShoppingOutlined, PlusOutlined, ImportOutlined, ShopOutlined, LinkOutlined } from '@ant-design/icons';
import OrdersList from '../components/orders/OrdersList';
import OrdersDashboard from '../components/orders/OrdersDashboard';
import DeliveriesMap from '../components/deliveries/DeliveriesMap';
import NewOrderModal from '../components/orders/NewOrderModal';
import ImportOrdersModal from '../components/orders/ImportOrdersModal';
import api from '../utils/api';

const { Title } = Typography;
const { TabPane } = Tabs;

const Orders = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNewOrderModalVisible, setIsNewOrderModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('dashboard');
  // État pour le chargement du lien public des commandes du jour
  const [copyingLink, setCopyingLink] = useState(false);

  // Lire l'onglet actif depuis l'URL au chargement
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && ['dashboard', 'orders'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);

  const showNewOrderModal = () => {
    setIsNewOrderModalVisible(true);
  };

  const handleNewOrderModalCancel = () => {
    setIsNewOrderModalVisible(false);
  };

  const handleNewOrderSuccess = () => {
    setIsNewOrderModalVisible(false);
    setRefreshKey(prevKey => prevKey + 1);
  };

  const showImportModal = () => {
    setIsImportModalVisible(true);
  };

  const handleImportModalCancel = () => {
    setIsImportModalVisible(false);
  };

  const handleImportSuccess = () => {
    setIsImportModalVisible(false);
    setRefreshKey(prevKey => prevKey + 1);
  };

  /**
   * Copie le lien public des commandes du jour dans le presse-papiers
   * Récupère le token via l'API authentifiée puis construit l'URL publique
   */
  const handleCopyDailyOrdersLink = async () => {
    setCopyingLink(true);
    try {
      const response = await api.get('/daily-orders/my-token');
      const token = response.data.token;
      // Construire l'URL publique avec l'origine du client
      const publicUrl = `${window.location.origin}/daily-orders/${token}`;
      await navigator.clipboard.writeText(publicUrl);
      message.success('Lien des commandes du jour copié !');
    } catch (err) {
      console.error('Erreur lors de la récupération du lien:', err);
      message.error('Impossible de récupérer le lien');
    } finally {
      setCopyingLink(false);
    }
  };

  // Gérer le changement d'onglet et mettre à jour l'URL
  const handleTabChange = (key) => {
    setActiveTab(key);
    
    // Mettre à jour l'URL avec le nouvel onglet en préservant les autres query params
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div className="w-full px-3 py-3 sm:px-6 sm:py-6 lg:px-8">
      {/* Mobile : titre puis boutons en dessous (évite le titre coupé « Commande s ») */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Breadcrumb
            items={[
              {
                href: '/',
                title: <HomeOutlined />,
              },
              {
                title: 'Commandes',
              },
            ]}
            className="mb-2"
          />
          <Title level={window.innerWidth < 768 ? 4 : 2} className="m-0 whitespace-nowrap">
            <span className="hidden sm:inline">Gestion des </span>Commandes
          </Title>
        </div>
        <Space wrap>
          {/* Bouton pour copier le lien public des commandes du jour (partageable) */}
          <Tooltip title="Copier le lien public des commandes du jour (partageable avec livreurs, partenaires...)">
            <Button
              icon={<LinkOutlined />}
              size="large"
              onClick={handleCopyDailyOrdersLink}
              loading={copyingLink}
            >
              <span className="hidden sm:inline">Lien du jour</span>
            </Button>
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={showNewOrderModal}
          >
            Nouvelle Commande
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={handleTabChange} size="large">
        <TabPane 
          tab={
            <span>
              <ShoppingOutlined />
              Tableau de bord
            </span>
          } 
          key="dashboard"
        >
          <OrdersDashboard key={`dashboard-${refreshKey}`} />
        </TabPane>
        <TabPane 
          tab={
            <span>
              <ShoppingOutlined />
              Commandes
            </span>
          } 
          key="orders"
        >
          <OrdersList key={`orders-${refreshKey}`} />
        </TabPane>
        {/* <TabPane 
          tab={
            <span>
              <ShoppingOutlined />
              Livraisons
            </span>
          } 
          key="deliveries"
        >
          <DeliveriesMap key={`deliveries-${refreshKey}`} />
        </TabPane> */}
      </Tabs>

      <NewOrderModal 
        visible={isNewOrderModalVisible} 
        onCancel={handleNewOrderModalCancel}
        onSuccess={handleNewOrderSuccess}
      />

      <ImportOrdersModal
        visible={isImportModalVisible}
        onCancel={handleImportModalCancel}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
};

export default Orders;
